import React, { useState, ChangeEvent } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  ExploreCategory,
  ExpirationDate,
  KlydoEntry,
  UpdateExploreCategoryPayload,
} from "../../../api/explore-categories";
import useUpdateExploreCategoryMutation from "../hooks/useUpdateExploreCategoryMutation";
import uploadFile from "../../../utils/uploadFiles";
import ButtonLoader from "../../../components/Widgets/ButtonLoader";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import DSButton from "../../system-design/DSButton";
import useKlydo from "../../../components/hooks/useKlydo";
import { isVideo, resizeCloudinary } from "../../../utils/util";
import KlydoModal from "../../Klydos/components/KlydoModal";
import DSDialog from "../../system-design/DSDialog";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSTextField from "../../system-design/DSTextField";
import DSTypography from "../../system-design/DSTypography";
import DSList from "../../system-design/DSList";
import DSListItem from "../../system-design/DSListItem";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSCard from "../../system-design/DSCard";
import DSUploadButton from "../../system-design/DSUploadButton";
import {
  IconButton,
  Switch,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
} from "@mui/material";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const toDateInputValue = (e: ExpirationDate): string =>
  `${e.year}-${String(e.month).padStart(2, "0")}-${String(e.day).padStart(2, "0")}`;

const fromDateInputValue = (value: string): ExpirationDate => {
  const [year, month, day] = value.split("-").map(Number);
  return { year, month, day };
};

const isExpirationPast = (e: ExpirationDate): boolean => {
  const now = new Date();
  const today = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
  if (e.year < today.year) return true;
  if (e.year > today.year) return false;
  if (e.month < today.month) return true;
  if (e.month > today.month) return false;
  return e.day <= today.day;
};

interface KlydoListItemProps {
  klydoId: string;
  onRemove?: (klydoId: string) => void;
  dragHandle?: React.ReactNode;
  favorites?: number;
  expiresAt?: ExpirationDate;
  onSetExpiry?: (klydoId: string, date: ExpirationDate | null) => void;
}

const KlydoListItem: React.FC<KlydoListItemProps> = ({
  klydoId,
  onRemove,
  dragHandle,
  favorites,
  expiresAt,
  onSetExpiry,
}) => {
  const [showKlydoModal, setShowKlydoModal] = useState(false);
  const { data: klydo, isLoading } = useKlydo(klydoId);

  return (
    <>
      {klydo && !isLoading && (
        <DSListItem
          key={klydo?.id}
          onClick={() => setShowKlydoModal(true)}
          sx={{
            border: 0,
            paddingTop: 0,
            paddingBottom: 0,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <DSBox
            sx={{
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            {dragHandle}
            <DSBox sx={{ flex: "0 0 22%" }} title={klydo?.id}>
              {klydo?.name}
            </DSBox>
            <DSBox sx={{ flex: "0 0 18%" }} title={klydo?.creator}>
              {klydo?.author?.name || klydo?.creator}
            </DSBox>
            <DSBox
              sx={{
                flex: "0 0 10%",
                textAlign: "center",
                color: favorites ? "inherit" : "#aaa",
              }}
            >
              {favorites !== undefined ? `♥ ${favorites}` : "—"}
            </DSBox>
            {!klydo?.loopUrl || isVideo(klydo?.loopUrl) === "none" ? (
              <DSBox
                sx={{
                  flex: "0 0 10%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <DSTypography>--</DSTypography>
              </DSBox>
            ) : (
              <DSBox
                sx={{
                  flex: "0 0 10%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <DSBox
                  sx={{
                    borderRadius: "50%",
                    width: 35,
                    height: 35,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <DSBox
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      position: "absolute",
                      top: "0%",
                      left: "0%",
                      width: "100%",
                      aspectRatio: "1/1",
                    }}
                  >
                    {isVideo(klydo?.loopUrl) === "image" ? (
                      <img
                        loading="lazy"
                        alt="gif"
                        src={resizeCloudinary(klydo?.loopUrl, 48)}
                        style={{
                          width: "auto",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <video
                        muted={true}
                        src={resizeCloudinary(klydo?.loopUrl, 48)}
                        loop
                        autoPlay
                        style={{
                          width: "auto",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </DSBox>
                </DSBox>
              </DSBox>
            )}
            {onSetExpiry && (
              <DSBox
                sx={{
                  flex: "0 0 22%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 0.5,
                  pl: 0.5,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <input
                    type="date"
                    value={expiresAt ? toDateInputValue(expiresAt) : ""}
                    onChange={(e) =>
                      onSetExpiry(
                        klydo.id,
                        e.target.value
                          ? fromDateInputValue(e.target.value)
                          : null,
                      )
                    }
                    style={{
                      fontSize: "11px",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      border: `1px solid ${expiresAt && isExpirationPast(expiresAt) ? "#d32f2f" : "#ccc"}`,
                      color:
                        expiresAt && isExpirationPast(expiresAt)
                          ? "#d32f2f"
                          : "inherit",
                      width: "100%",
                    }}
                  />
                  {expiresAt && (
                    <IconButton
                      size="small"
                      title="Clear expiry"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetExpiry(klydo.id, null);
                      }}
                      sx={{ width: 18, height: 18, flexShrink: 0 }}
                    >
                      <i
                        className="nc-icon nc-simple-remove"
                        style={{ fontSize: 8 }}
                      />
                    </IconButton>
                  )}
                </DSBox>
                {expiresAt && isExpirationPast(expiresAt) && (
                  <Chip
                    label="Expired"
                    size="small"
                    color="error"
                    sx={{
                      fontSize: "10px",
                      height: 16,
                      width: "fit-content",
                    }}
                  />
                )}
              </DSBox>
            )}
            {onRemove && (
              <DSBox
                sx={{
                  flex: "0 0 10%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  title="Remove klydo"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(klydo.id);
                  }}
                  sx={{ width: 24, height: 24 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DSBox>
            )}
          </DSBox>
        </DSListItem>
      )}

      {showKlydoModal && klydo && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={showKlydoModal}
          onClose={() => setShowKlydoModal(false)}
        >
          <KlydoModal klydoId={klydo.id} setShowModal={setShowKlydoModal} />
        </DSDialog>
      )}
    </>
  );
};

const ExploreCategoryModal = (props: {
  row?: ExploreCategory;
  setShowModal: (b: boolean) => void;
}) => {
  const [logoUrl, setLogoUrl] = useState<string>(props?.row?.logoUrl || "");
  const [name, setName] = useState<string>(props.row?.name || "");
  const [description, setDescription] = useState<string>(
    props.row?.description || "",
  );
  const [active, setActive] = useState<boolean>(props.row?.active ?? false);
  const [draftKlydoEntries, setDraftKlydoEntries] = useState<KlydoEntry[]>(
    props?.row?.klydos?.map((k) => ({
      klydoId: k.id,
      expiresAt: k.expiresAt,
    })) ?? [],
  );
  const [contentChangedTimestamp, setContentChangedTimestampRaw] = useState<
    number | null
  >(props.row?.contentChangedTimestamp ?? null);
  const hasManuallyChangedTimestamp =
    contentChangedTimestamp !== (props.row?.contentChangedTimestamp ?? null);
  const [markAsUpdatedOnSave, setMarkAsUpdatedOnSave] = useState(false);
  const setContentChangedTimestamp = (value: number | null) => {
    setContentChangedTimestampRaw(value);
    setMarkAsUpdatedOnSave(false);
  };
  const [autoFill, setAutoFill] = useState<boolean>(
    props.row?.autoFill ?? false,
  );
  const [autoFillQuery, setAutoFillQuery] = useState<string>(
    props.row?.autoFillQuery
      ? JSON.stringify(props.row.autoFillQuery, null, 2)
      : "",
  );
  const [autoFillSort, setAutoFillSort] = useState<string>(
    props.row?.autoFillSort
      ? JSON.stringify(props.row.autoFillSort, null, 2)
      : "",
  );
  const [autoFillLimit, setAutoFillLimit] = useState<number | null>(
    props.row?.autoFillLimit ?? null,
  );
  const [queryError, setQueryError] = useState<string>("");
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [isKlydoModalOpen, setIsKlydoModalOpen] = useState(false);
  const { mutate: updateExploreCategoryMutation } =
    useUpdateExploreCategoryMutation({
      onSuccess: () => {
        props.setShowModal(false);
      },
    });

  const klydoFavoritesMap = Object.fromEntries(
    (props.row?.klydos ?? []).map((k) => [k.id, k.favorites ?? 0]),
  );

  const expiresAtMap = Object.fromEntries(
    draftKlydoEntries
      .filter((e) => e.expiresAt)
      .map((e) => [e.klydoId, e.expiresAt]),
  );

  const handleRemoveKlydo = (klydoId: string) => {
    setDraftKlydoEntries((prev) => prev.filter((e) => e.klydoId !== klydoId));
  };

  const handleSetExpiry = (klydoId: string, date: ExpirationDate | null) => {
    setDraftKlydoEntries((prev) =>
      prev.map((e) =>
        e.klydoId === klydoId ? { ...e, expiresAt: date ?? undefined } : e,
      ),
    );
  };

  const handleKlydoSelection = (selected: string[]) => {
    setDraftKlydoEntries((prev) => {
      const existingMap = new Map(prev.map((e) => [e.klydoId, e]));
      return selected.map((id) => existingMap.get(id) ?? { klydoId: id });
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const reordered = Array.from(draftKlydoEntries);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setDraftKlydoEntries(reordered);
  };

  const validateAutoFillJson = (): {
    parsedQuery: Record<string, unknown> | null;
    parsedSort: Record<string, unknown> | null;
  } | null => {
    let parsedQuery: Record<string, unknown> | null = null;
    let parsedSort: Record<string, unknown> | null = null;

    if (autoFillQuery) {
      try {
        const parsed = JSON.parse(autoFillQuery);
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          setQueryError("Query must be a JSON object");
          return null;
        }
        parsedQuery = parsed;
      } catch {
        setQueryError("Invalid query JSON");
        return null;
      }
    }
    if (autoFillSort) {
      try {
        const parsed = JSON.parse(autoFillSort);
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          setQueryError("Sort must be a JSON object");
          return null;
        }
        parsedSort = parsed;
      } catch {
        setQueryError("Invalid sort JSON");
        return null;
      }
    }
    return { parsedQuery, parsedSort };
  };

  const handleSaveClick = () => {
    if (autoFill) {
      const result = validateAutoFillJson();
      if (!result) return;
    }
    setShowSaveModal(true);
  };

  const saveExploreCategory = async () => {
    const mutation: UpdateExploreCategoryPayload & { id: string } = {
      id: props.row!.id,
      name,
      description,
      logoUrl,
      active,
      autoFill,
      contentChangedTimestamp: markAsUpdatedOnSave
        ? Date.now()
        : contentChangedTimestamp,
    };

    if (autoFill) {
      const result = validateAutoFillJson();
      if (!result) return;
      mutation.autoFillQuery = result.parsedQuery;
      mutation.autoFillSort = result.parsedSort;
      mutation.autoFillLimit = autoFillLimit;
    } else {
      mutation.klydos = draftKlydoEntries;
      mutation.autoFillQuery = null;
      mutation.autoFillSort = null;
      mutation.autoFillLimit = null;
    }

    updateExploreCategoryMutation(mutation);
  };

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 0,
          marginLeft: "10px",
          marginRight: "5px",
          marginBottom: "24px",
        }}
      >
        <DSTypography
          variant="h4"
          sx={{ margin: 0, paddingTop: "16px", paddingLeft: "14px" }}
        >
          Explore Category
        </DSTypography>
        <IconButton
          onClick={() => {
            props.setShowModal(false);
          }}
          sx={{ bottom: "7px", position: "relative" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DSBox>
      <DSBox>
        <DSBox sx={{ padding: 2 }}>
          <DSGrid container spacing={2}>
            <DSGrid item xs={12}>
              <form onSubmit={saveExploreCategory} id="exploreCategoryForm">
                <DSGrid container spacing={2}>
                  <DSGrid
                    item
                    sx={{
                      width: "80%",
                      mx: "auto",
                      backgroundColor: "paper.elevation4",
                      p: 2,
                      borderRadius: "16px",
                      mb: 2,
                    }}
                  >
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                        Name:
                      </DSTypography>
                      <DSBox sx={{ flex: 1 }}>
                        <DSTextField
                          value={name}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setName(e.target.value)
                          }
                          placeholder="Enter explore category name..."
                          required
                          fullWidth
                          size="small"
                        />
                      </DSBox>
                    </DSBox>
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                        Description:
                      </DSTypography>
                      <DSBox sx={{ flex: 1 }}>
                        <DSTextField
                          value={description}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setDescription(e.target.value)
                          }
                          placeholder="Enter description..."
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                        />
                      </DSBox>
                    </DSBox>
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={autoFill}
                            onChange={(e) => {
                              setAutoFill(e.target.checked);
                              setQueryError("");
                            }}
                          />
                        }
                        label="Auto-Fill"
                      />
                    </DSBox>
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: 2,
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                        Content Updated:
                      </DSTypography>
                      <DSTypography sx={{ minWidth: 160 }}>
                        {contentChangedTimestamp != null
                          ? new Date(contentChangedTimestamp).toLocaleString()
                          : "Not set"}
                      </DSTypography>
                      <DSButton
                        variant="outlined"
                        size="small"
                        type="button"
                        onClick={() => setContentChangedTimestamp(Date.now())}
                      >
                        Mark as Updated
                      </DSButton>
                      <input
                        type="datetime-local"
                        value={
                          contentChangedTimestamp != null
                            ? new Date(
                                contentChangedTimestamp -
                                  new Date().getTimezoneOffset() * 60000,
                              )
                                .toISOString()
                                .slice(0, 16)
                            : ""
                        }
                        onChange={(e) =>
                          setContentChangedTimestamp(
                            e.target.value
                              ? new Date(e.target.value).getTime()
                              : null,
                          )
                        }
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                        }}
                      />
                      {contentChangedTimestamp != null && (
                        <DSButton
                          variant="text"
                          size="small"
                          type="button"
                          color="error"
                          onClick={() => setContentChangedTimestamp(null)}
                        >
                          Clear
                        </DSButton>
                      )}
                    </DSBox>
                  </DSGrid>

                  <DSGrid
                    item
                    sx={{
                      width: "80%",
                      mx: "auto",
                      backgroundColor: "paper.elevation4",
                      p: 2,
                      borderRadius: "16px",
                      mb: 2,
                    }}
                  >
                    <DSCard
                      sx={{ mb: 2, p: 2, textAlign: "center", width: "100%" }}
                    >
                      <DSBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <label style={{ width: "100%" }}>
                          <DSUploadButton
                            text={logoUrl ? "Change Logo" : "Upload Logo"}
                          />
                          <input
                            type="file"
                            hidden
                            accept="image/png"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const fileReader = new FileReader();
                                fileReader.onload = () => {
                                  uploadFile({
                                    file: fileReader.result as string,
                                    folder: "explore-category-logo",
                                    upload_preset: "collection_logo",
                                  }).then((url) => setLogoUrl(url as string));
                                };
                                fileReader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      </DSBox>
                      {logoUrl && (
                        <DSBox>
                          <DSTypography
                            color="text.secondary"
                            variant="caption"
                          >
                            Current Logo Preview:
                          </DSTypography>
                          <br />
                          <img
                            src={logoUrl}
                            alt="Explore category logo preview"
                            style={{
                              maxWidth: "80px",
                              maxHeight: "80px",
                              borderRadius: "8px",
                              marginTop: "5px",
                              backgroundColor: "#333",
                              padding: "4px",
                            }}
                          />
                        </DSBox>
                      )}
                    </DSCard>
                  </DSGrid>

                  {autoFill && (
                    <DSGrid
                      item
                      sx={{
                        width: "80%",
                        mx: "auto",
                        backgroundColor: "paper.elevation4",
                        p: 2,
                        borderRadius: "16px",
                        mt: 2,
                      }}
                    >
                      <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                        Auto-Fill Configuration:
                      </DSTypography>
                      {queryError && (
                        <Alert
                          severity="error"
                          sx={{ mb: 2 }}
                          onClose={() => setQueryError("")}
                        >
                          {queryError}
                        </Alert>
                      )}
                      <DSBox sx={{ mb: 2 }}>
                        <DSTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          MongoDB Query (filter against klydos collection)
                        </DSTypography>
                        <DSTextField
                          value={autoFillQuery}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setAutoFillQuery(e.target.value);
                            setQueryError("");
                          }}
                          placeholder={'e.g. { "tags": "nature" }'}
                          fullWidth
                          size="small"
                          multiline
                          rows={6}
                          sx={{ fontFamily: "monospace" }}
                        />
                      </DSBox>
                      <DSBox sx={{ mb: 2 }}>
                        <DSTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Sort (optional)
                        </DSTypography>
                        <DSTextField
                          value={autoFillSort}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setAutoFillSort(e.target.value);
                            setQueryError("");
                          }}
                          placeholder={'e.g. { "stats.favorites": -1 }'}
                          fullWidth
                          size="small"
                          multiline
                          rows={3}
                          sx={{ fontFamily: "monospace" }}
                        />
                      </DSBox>
                      <DSBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <DSTypography variant="caption" color="text.secondary">
                          Limit (optional):
                        </DSTypography>
                        <DSTextField
                          type="number"
                          value={autoFillLimit ?? ""}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setAutoFillLimit(
                              e.target.value ? Number(e.target.value) : null,
                            )
                          }
                          placeholder="e.g. 20"
                          size="small"
                          sx={{ width: 120 }}
                        />
                      </DSBox>
                    </DSGrid>
                  )}

                  <DSGrid
                    item
                    sx={{
                      width: "80%",
                      mx: "auto",
                      backgroundColor: "paper.elevation4",
                      p: 2,
                      borderRadius: "16px",
                      mt: 2,
                    }}
                  >
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                        Klydos:
                      </DSTypography>
                      {autoFill && (
                        <Chip
                          label="Managed by auto-fill cron"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </DSBox>
                    {draftKlydoEntries.length > 0 ? (
                      <DSCard sx={{ mb: 2, p: 2, width: "100%" }}>
                        <DSList
                          sx={{
                            width: "100%",
                            fontWeight: "bold",
                          }}
                        >
                          <DSListItem>
                            <DSBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "12px",
                                color: "gray",
                                border: 0,
                                width: "100%",
                              }}
                            >
                              {!autoFill && <DSBox sx={{ width: 28 }} />}
                              <DSBox sx={{ flex: "0 0 22%" }}>Name</DSBox>
                              <DSBox sx={{ flex: "0 0 18%" }}>Author</DSBox>
                              <DSBox
                                sx={{ flex: "0 0 10%", textAlign: "center" }}
                              >
                                Favorites
                              </DSBox>
                              <DSBox
                                sx={{ flex: "0 0 10%", textAlign: "center" }}
                              >
                                Preview
                              </DSBox>
                              {!autoFill && (
                                <DSBox
                                  sx={{ flex: "0 0 22%", textAlign: "center" }}
                                >
                                  Expires
                                </DSBox>
                              )}
                              {!autoFill && (
                                <DSBox
                                  sx={{ flex: "0 0 10%", textAlign: "center" }}
                                ></DSBox>
                              )}
                            </DSBox>
                          </DSListItem>
                        </DSList>
                        {autoFill ? (
                          <DSList
                            sx={{
                              width: "100%",
                              borderRadius: 0,
                              border: "none",
                              maxHeight: "190px",
                              overflowY: "auto",
                            }}
                          >
                            {draftKlydoEntries.map(({ klydoId }) => (
                              <KlydoListItem
                                key={klydoId}
                                klydoId={klydoId}
                                favorites={klydoFavoritesMap[klydoId]}
                              />
                            ))}
                          </DSList>
                        ) : (
                          <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="explore-category-klydos">
                              {(droppableProvided) => (
                                <DSList
                                  ref={droppableProvided.innerRef}
                                  {...droppableProvided.droppableProps}
                                  sx={{
                                    width: "100%",
                                    borderRadius: 0,
                                    border: "none",
                                    maxHeight: "190px",
                                    overflowY: "auto",
                                  }}
                                >
                                  {draftKlydoEntries.map(
                                    ({ klydoId }, index) => (
                                      <Draggable
                                        key={klydoId}
                                        draggableId={klydoId}
                                        index={index}
                                      >
                                        {(
                                          draggableProvided,
                                          draggableSnapshot,
                                        ) => (
                                          <DSBox
                                            ref={draggableProvided.innerRef}
                                            {...draggableProvided.draggableProps}
                                            sx={{
                                              ...(draggableSnapshot.isDragging && {
                                                bgcolor: "action.hover",
                                                boxShadow: 2,
                                              }),
                                            }}
                                          >
                                            <KlydoListItem
                                              klydoId={klydoId}
                                              onRemove={handleRemoveKlydo}
                                              favorites={
                                                klydoFavoritesMap[klydoId]
                                              }
                                              expiresAt={expiresAtMap[klydoId]}
                                              onSetExpiry={handleSetExpiry}
                                              dragHandle={
                                                <DSBox
                                                  {...draggableProvided.dragHandleProps}
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    cursor: "grab",
                                                    color: "text.secondary",
                                                    mr: 1,
                                                  }}
                                                >
                                                  <DragIndicatorIcon
                                                    sx={{ fontSize: 16 }}
                                                  />
                                                </DSBox>
                                              }
                                            />
                                          </DSBox>
                                        )}
                                      </Draggable>
                                    ),
                                  )}
                                  {droppableProvided.placeholder}
                                </DSList>
                              )}
                            </Droppable>
                          </DragDropContext>
                        )}
                      </DSCard>
                    ) : (
                      <DSTypography>No klydos in explore category</DSTypography>
                    )}
                    {!autoFill && (
                      <DSBox
                        sx={{
                          width: "90%",
                          flexDirection: "column",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <DSBox
                          sx={{
                            width: "90%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            margin: "0 10px 10px 10px",
                          }}
                        >
                          <DSButton
                            type="button"
                            onClick={() => {
                              setIsKlydoModalOpen(true);
                            }}
                          >
                            Select Klydos
                          </DSButton>
                        </DSBox>
                      </DSBox>
                    )}
                  </DSGrid>

                  <DSGrid item xs={12}>
                    <DSBox
                      sx={{
                        display: "flex",
                        justifyContent: "right",
                        marginTop: "5px",
                      }}
                    >
                      <DSPrimaryButton
                        disabled={!name}
                        onClick={handleSaveClick}
                      >
                        Save
                      </DSPrimaryButton>

                      {showSaveModal && (
                        <DSDialog
                          onClose={() => setShowSaveModal(false)}
                          open={showSaveModal}
                          maxWidth="sm"
                          fullWidth
                        >
                          <DSDialogContent>
                            <DSBox sx={{ margin: 3 }}>
                              <DSTypography
                                variant="h6"
                                sx={{ textAlign: "center", mb: 3 }}
                              >
                                You are about to save changes for {name}
                              </DSTypography>
                              {!hasManuallyChangedTimestamp && (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={markAsUpdatedOnSave}
                                      onChange={(e) =>
                                        setMarkAsUpdatedOnSave(e.target.checked)
                                      }
                                    />
                                  }
                                  label="Also mark as updated in clocks"
                                />
                              )}
                            </DSBox>
                            <DSGrid
                              container
                              spacing={2}
                              justifyContent="space-between"
                            >
                              <DSGrid item xs={5}>
                                <DSButton
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => setShowSaveModal(false)}
                                >
                                  Cancel
                                </DSButton>
                              </DSGrid>
                              <DSGrid item xs={5}>
                                <ButtonLoader
                                  variant="outlined"
                                  className="w-100 h-100"
                                  title="Save"
                                  onClick={() => saveExploreCategory()}
                                />
                              </DSGrid>
                            </DSGrid>
                          </DSDialogContent>
                        </DSDialog>
                      )}
                    </DSBox>
                  </DSGrid>
                </DSGrid>
              </form>
            </DSGrid>
          </DSGrid>
        </DSBox>
      </DSBox>

      {isKlydoModalOpen && (
        <KlydosPickDialog
          selectedKlydos={draftKlydoEntries.map((e) => e.klydoId)}
          onPickKlydos={handleKlydoSelection}
          onClose={() => setIsKlydoModalOpen(false)}
        />
      )}
    </>
  );
};

export default ExploreCategoryModal;
